// SetBackdropNode — the editor face of the `setBackdrop` event node.
//
// The backdrop (sky colour + ground plane) had no node, which is why a viewport
// look could not be saved to a character: the camera half already had
// setCameraFov / Target / Cage / SeeThrough / Follow, so a Scene Look group
// could carry the camera and nothing else. This is the missing half, and it
// completes what sceneLookNodes.js writes.
//
// Self-contained rather than reusing CameraNodes.jsx's Shell, which is
// file-local there and camera-accented. Same precedent as LoadEnvironmentNode:
// a scene node carries scene colours.
//
// Runtime: EventRuntime.js's `setBackdrop` case → liveTuneStore.setStateAuthored,
// so the value is marked blueprint-authored and stripped from localStorage
// (tuneAuthored.js) instead of masquerading as the dev's own setting next load.

import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { selectedBorder } from './selectedBorder.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'

const HEADER_BG     = '#0a3028'
const HEADER_ACTIVE = '#10503a'
const ACCENT        = '#40d090'
const BORDER_SEL    = '#40d090'
const BORDER_IDLE   = '#0d4030'
const execStyle = { background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }
const stop = e => e.stopPropagation()

const ROW = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 5 }
const LABEL = { color: '#60d090', fontSize: 9, letterSpacing: 0.3 }

function CheckRow({ label, checked, onChange }) {
  return (
    <label style={{ ...ROW, cursor: 'pointer' }} onPointerDown={stop} onMouseDown={stop}>
      <span style={LABEL}>{label}</span>
      <input className={nodeControlClass()} type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
    </label>
  )
}

function ColorRow({ label, value, onChange, disabled }) {
  return (
    // Shown-and-disabled rather than hidden when its toggle is off: an
    // unavailable control that vanishes reads as a missing feature
    // (voiceInputState.js's rule).
    <div style={{ ...ROW, opacity: disabled ? 0.4 : 1 }} onPointerDown={stop} onMouseDown={stop}>
      <span style={LABEL}>{label}</span>
      <input className={nodeControlClass()} type="color" value={value ?? '#000000'} disabled={disabled}
        onChange={e => onChange(e.target.value)}
        style={{ width: 44, height: 18, padding: 0, border: '1px solid #0d3020', background: 'none' }} />
    </div>
  )
}

function NumRow({ label, value, onChange, disabled }) {
  return (
    <div style={{ ...ROW, opacity: disabled ? 0.4 : 1 }} onPointerDown={stop} onMouseDown={stop}>
      <span style={LABEL}>{label}</span>
      <input className={nodeControlClass()} type="number" value={value} disabled={disabled} step={1}
        onChange={e => onChange(Number(e.target.value))}
        style={{
          width: 56, background: '#020a06', border: '1px solid #0d3020', color: '#60d090',
          padding: '2px 4px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
        }} />
    </div>
  )
}

export default function SetBackdropNode({ data, selected }) {
  const fired = isFired(data)
  const set = (k, v) => data.onChange?.(k, v)
  const sky = !!data.skyEnabled
  const ground = !!data.groundEnabled
  return (
    <div
      title="Set the viewport backdrop — the sky colour behind everything and an opaque ground plane at the stage floor. Both are part of the rendered frame, so they show up in a recording or a canvas readback. Saved into a character by Settings ▸ Scene."
      style={{
        background: selected ? '#081a12' : '#050e0a',
        border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(BORDER_IDLE) : BORDER_IDLE}`,
        borderRadius: 6, minWidth: 220, fontFamily: 'var(--m-font)',
        boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
      }}
    >
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn" style={execStyle} />
        <span style={{ fontSize: 13 }}>🎨</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Set Backdrop</span>
        <Handle type="source" position={Position.Right} id="execOut" style={execStyle} />
      </div>

      <div className="nodrag" style={{ padding: '6px 12px 10px' }}>
        <CheckRow label="SKY" checked={sky} onChange={v => set('skyEnabled', v)} />
        <ColorRow label="SKY COLOR" value={data.skyColor} disabled={!sky}
          onChange={v => set('skyColor', v)} />

        <CheckRow label="GROUND" checked={ground} onChange={v => set('groundEnabled', v)} />
        <ColorRow label="GROUND COLOR" value={data.groundColor} disabled={!ground}
          onChange={v => set('groundColor', v)} />
        <NumRow label="GROUND SIZE (m)" value={data.groundSize ?? 20} disabled={!ground}
          onChange={v => set('groundSize', v)} />

        <CheckRow label="HIDE ENVIRONMENT" checked={!!data.hideEnvironment}
          onChange={v => set('hideEnvironment', v)} />
      </div>
    </div>
  )
}
